import React from 'react';
import { 
  TrendingUp, TrendingDown, 
  ArrowUp, ArrowDown,
  Star, Wrench, ClipboardCheck, Clock, Shield, DollarSign
} from 'lucide-react';

const ScoreCard = ({ title, score, maxScore, icon: Icon, color, change, details }) => {
  const percentage = (score / maxScore) * 100;
  
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center">
          <div className={`p-2.5 rounded-lg ${color.bg} bg-opacity-20`}>
            <Icon className={`h-5 w-5 ${color.text}`} />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">{title}</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-gray-900 mr-1">{score}</p>
              <p className="text-sm text-gray-500">/{maxScore}</p>
            </div>
          </div>
        </div>
        {change && (
          <div className={`flex items-center text-xs font-medium ${
            change.trend === 'up' 
              ? 'text-emerald-600' 
              : 'text-rose-600'
          }`}>
            {change.trend === 'up' ? (
              <ArrowUp className="h-3 w-3 mr-0.5" />
            ) : (
              <ArrowDown className="h-3 w-3 mr-0.5" />
            )}
            {change.value}
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="relative pt-1">
          <div className="overflow-hidden h-2 mb-2 text-xs flex rounded bg-gray-100">
            <div 
              style={{ width: `${percentage}%` }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${color.bar}`}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div>Score</div>
            <div>{Math.round(percentage)}%</div>
          </div>
        </div>
      </div>

      {details && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-3">
            {details.map((detail, index) => (
              <div key={index} className="flex justify-between items-baseline">
                <span className="text-xs text-gray-500">{detail.label}</span>
                <span className="text-xs font-medium text-gray-900">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricTrend = ({ label, current, previous, prefix = "", suffix = "", isPercentage = false }) => {
  const calculateChange = () => {
    if (previous === 0) return Infinity;
    return ((current - previous) / previous) * 100;
  };

  const change = calculateChange();
  const isImprovement = change > 0;
  
  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-baseline">
        <span className="text-sm text-gray-500">{label}</span>
        {isFinite(change) && (
          <span className={`text-xs font-medium flex items-center ${
            isImprovement ? 'text-emerald-600' : 'text-rose-600'
          }`}>
            {isImprovement ? (
              <TrendingUp className="h-3 w-3 mr-1" />
            ) : (
              <TrendingDown className="h-3 w-3 mr-1" />
            )}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="text-lg font-semibold mt-1">{prefix}{isPercentage ? `${current}%` : current}{suffix}</div>
      <div className="text-xs text-gray-400 mt-1">Previous: {prefix}{isPercentage ? `${previous}%` : previous}{suffix}</div>
    </div>
  );
};

const Overview = ({ data, stores, period, region, setRegionFilter, setPeriodFilter }) => {
  if (!data) return null;

  const {
    totalScore,
    previousTotalScore,
    salesScore,
    reviewsScore,
    maintenanceScore,
    auditScore,
    speedScore,
    qualityScore,
    salesMetrics,
    maintenanceMetrics,
    reviewMetrics,
    serviceMetrics
  } = data;

  const totalChange = {
    trend: totalScore >= previousTotalScore ? 'up' : 'down',
    value: `${Math.abs(totalScore - previousTotalScore).toFixed(1)} pts`
  };

  // Format the period label for display
  const getPeriodLabel = () => {
    switch(period) {
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'quarter': return 'This Quarter';
      case 'year': return 'This Year';
      default: return 'This Month';
    }
  };

  // Format the region label for display
  const getRegionLabel = () => {
    if (region === 'all') return 'All Regions';
    const regionMap = {
      'central': 'Central Region',
      'western': 'Western Region', 
      'eastern': 'Eastern Region',
      'southern': 'Southern Region',
      'northern': 'Northern Region'
    };
    return regionMap[region] || 'All Regions';
  };

  return (
    <div className="space-y-6">
      {/* Main Scorecard */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Overall Performance</h2>
            <p className="text-sm text-gray-600">
              {getRegionLabel()} - {getPeriodLabel()}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <div className="text-3xl font-bold text-gray-900">{totalScore.toFixed(1)}</div>
            <div className="flex items-center text-sm">
              <span className="text-gray-500 mr-1">out of 100</span>
              <span className={`flex items-center font-medium ${
                totalChange.trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
              }`}>
                {totalChange.trend === 'up' ? (
                  <ArrowUp className="h-3 w-3 mr-0.5" />
                ) : (
                  <ArrowDown className="h-3 w-3 mr-0.5" />
                )}
                {totalChange.value}
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                  Score Breakdown
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full">
              {/* Sales Performance - 30% */}
              <div 
                style={{ width: `${(salesScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
              >
              </div>
              {/* Google Reviews - 20% */}
              <div 
                style={{ width: `${(reviewsScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-amber-500"
              >
              </div>
              {/* Maintenance - 10% */}
              <div 
                style={{ width: `${(maintenanceScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-rose-500"
              >
              </div>
              {/* Audit - 10% */}
              <div 
                style={{ width: `${(auditScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
              >
              </div>
              {/* Speed - 15% */}
              <div 
                style={{ width: `${(speedScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-emerald-500"
              >
              </div>
              {/* Quality - 15% */}
              <div 
                style={{ width: `${(qualityScore / totalScore) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-cyan-500"
              >
              </div>
            </div>
            <div className="flex text-xs font-medium">
              <div className="flex items-center mr-4">
                <div className="h-3 w-3 rounded-full bg-blue-500 mr-1"></div>
                <span className="text-gray-700">Sales</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="h-3 w-3 rounded-full bg-amber-500 mr-1"></div>
                <span className="text-gray-700">Reviews</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="h-3 w-3 rounded-full bg-rose-500 mr-1"></div>
                <span className="text-gray-700">Maintenance</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="h-3 w-3 rounded-full bg-purple-500 mr-1"></div>
                <span className="text-gray-700">Audit</span>
              </div>
              <div className="flex items-center mr-4">
                <div className="h-3 w-3 rounded-full bg-emerald-500 mr-1"></div>
                <span className="text-gray-700">Speed</span>
              </div>
              <div className="flex items-center">
                <div className="h-3 w-3 rounded-full bg-cyan-500 mr-1"></div>
                <span className="text-gray-700">Quality</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Score Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Sales Performance */}
        <ScoreCard 
          title="Sales Performance"
          score={salesScore}
          maxScore={30}
          icon={DollarSign}
          color={{
            bg: 'bg-blue-500',
            text: 'text-blue-500',
            bar: 'bg-blue-500'
          }}
          change={{
            trend: 'up',
            value: '+1.5'
          }}
          details={[
            { label: 'Sales Growth', value: `${salesMetrics.growth}%` },
            { label: 'Target Achievement', value: `${salesMetrics.targetAchievement}%` },
            { label: 'Average Transaction', value: `SAR ${salesMetrics.avgTransaction}` },
            { label: 'Customer Count', value: salesMetrics.customerCount.toLocaleString() }
          ]}
        />

        {/* Google Reviews */}
        <ScoreCard 
          title="Google Reviews"
          score={reviewsScore}
          maxScore={20}
          icon={Star}
          color={{
            bg: 'bg-amber-500',
            text: 'text-amber-500',
            bar: 'bg-amber-500'
          }}
          change={{
            trend: 'up',
            value: '+0.8'
          }}
          details={[
            { label: 'Average Rating', value: reviewMetrics.avgRating },
            { label: 'Total Reviews', value: reviewMetrics.totalReviews },
            { label: 'Positive Sentiment', value: `${reviewMetrics.positiveSentiment}%` },
            { label: 'Response Rate', value: `${reviewMetrics.responseRate}%` }
          ]}
        />

        {/* Maintenance */}
        <ScoreCard 
          title="Maintenance"
          score={maintenanceScore}
          maxScore={10}
          icon={Wrench}
          color={{
            bg: 'bg-rose-500',
            text: 'text-rose-500',
            bar: 'bg-rose-500'
          }}
          change={{
            trend: 'down',
            value: '-0.3'
          }}
          details={[
            { label: 'Active Issues', value: maintenanceMetrics.activeIssues },
            { label: 'Avg Resolution', value: `${maintenanceMetrics.avgResolutionDays}d` },
            { label: 'Preventive Maint.', value: `${maintenanceMetrics.preventiveMaintPct}%` },
            { label: 'Critical Issues', value: maintenanceMetrics.criticalIssues }
          ]}
        />

        {/* Audit Score */}
        <ScoreCard 
          title="Audit Score"
          score={auditScore}
          maxScore={10}
          icon={ClipboardCheck}
          color={{
            bg: 'bg-purple-500',
            text: 'text-purple-500',
            bar: 'bg-purple-500'
          }}
          change={{
            trend: 'up',
            value: '+0.5'
          }}
          details={[
            { label: 'Last Audit', value: '02 Jun' },
            { label: 'Cleanliness', value: `${data.auditMetrics.cleanliness}%` },
            { label: 'SOP Compliance', value: `${data.auditMetrics.sopCompliance}%` },
            { label: 'Safety', value: `${data.auditMetrics.safety}%` }
          ]}
        />

        {/* Speed of Service */}
        <ScoreCard 
          title="Speed of Service"
          score={speedScore}
          maxScore={15}
          icon={Clock}
          color={{
            bg: 'bg-emerald-500',
            text: 'text-emerald-500',
            bar: 'bg-emerald-500'
          }}
          change={{
            trend: 'up',
            value: '+0.9'
          }}
          details={[
            { label: 'Avg Service Time', value: `${serviceMetrics.avgServiceTime}s` },
            { label: 'Drive-thru Time', value: `${serviceMetrics.driveThruTime}s` },
            { label: 'Counter Time', value: `${serviceMetrics.counterTime}s` },
            { label: 'Order Accuracy', value: `${serviceMetrics.orderAccuracy}%` }
          ]}
        />

        {/* Quality Control */}
        <ScoreCard 
          title="Quality Control"
          score={qualityScore}
          maxScore={15}
          icon={Shield}
          color={{
            bg: 'bg-cyan-500',
            text: 'text-cyan-500',
            bar: 'bg-cyan-500'
          }}
          change={{
            trend: 'down',
            value: '-0.2'
          }}
          details={[
            { label: 'Product Quality', value: `${data.qualityMetrics.productQuality}%` },
            { label: 'Presentation', value: `${data.qualityMetrics.presentation}%` },
            { label: 'Recipe Adherence', value: `${data.qualityMetrics.recipeAdherence}%` },
            { label: 'Temperature', value: `${data.qualityMetrics.temperature}%` }
          ]}
        />
      </div>

      {/* Key Performance Metrics */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Metrics</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <MetricTrend 
            label="Daily Sales"
            current={salesMetrics.avgDailySales}
            previous={salesMetrics.prevAvgDailySales}
            prefix="SAR "
          />
          
          <MetricTrend 
            label="Transactions"
            current={salesMetrics.dailyTransactions}
            previous={salesMetrics.prevDailyTransactions}
          />
          
          <MetricTrend 
            label="Avg. Transaction"
            current={salesMetrics.avgTransaction}
            previous={salesMetrics.prevAvgTransaction}
            prefix="SAR "
          />
          
          <MetricTrend 
            label="Customer Growth"
            current={salesMetrics.customerGrowth}
            previous={salesMetrics.prevCustomerGrowth}
            isPercentage={true}
          />
          
          <MetricTrend 
            label="Google Rating"
            current={reviewMetrics.avgRating}
            previous={reviewMetrics.prevAvgRating}
            suffix="/5"
          />
          
          <MetricTrend 
            label="Service Speed"
            current={serviceMetrics.avgServiceTime}
            previous={serviceMetrics.prevAvgServiceTime}
            suffix="s"
          />
          
          <MetricTrend 
            label="Order Accuracy"
            current={serviceMetrics.orderAccuracy}
            previous={serviceMetrics.prevOrderAccuracy}
            isPercentage={true}
          />
          
          <MetricTrend 
            label="Active Maintenance"
            current={maintenanceMetrics.activeIssues}
            previous={maintenanceMetrics.prevActiveIssues}
          />
        </div>
      </div>

      {/* Reference Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Top Regions</h2>
            <p className="text-sm text-gray-500 mt-1">Ranked by average store performance</p>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              { name: 'Central Region', score: 89.4, stores: 32, topStore: 'Riyadh Central' },
              { name: 'Western Region', score: 83.6, stores: 28, topStore: 'Jeddah Beach' },
              { name: 'Eastern Region', score: 81.2, stores: 22, topStore: 'Dammam Mall' },
              { name: 'Southern Region', score: 79.8, stores: 14, topStore: 'Abha Heights' },
              { name: 'Northern Region', score: 77.5, stores: 8, topStore: 'Tabuk Central' }
            ].map((region, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">{region.name}</span>
                  <span className={`text-sm font-medium ${region.score >= 85 ? 'text-emerald-600' : region.score >= 75 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {region.score}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{region.stores} stores</span>
                  <span>Top: {region.topStore}</span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-1.5 rounded-full ${
                      region.score >= 85 ? 'bg-emerald-500' : 
                      region.score >= 75 ? 'bg-amber-500' : 
                      'bg-rose-500'
                    }`} 
                    style={{ width: `${region.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Most Improved</h2>
            <p className="text-sm text-gray-500 mt-1">Highest score increase this period</p>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              { name: 'Khamis Mushait Mall', region: 'Southern', score: 74.2, increase: 8.3 },
              { name: 'Al Khobar Corniche', region: 'Eastern', score: 82.7, increase: 7.1 },
              { name: 'Makkah Grand Avenue', region: 'Western', score: 79.5, increase: 6.8 },
              { name: 'Qassim University', region: 'Central', score: 85.2, increase: 5.9 },
              { name: 'Jubail Industrial', region: 'Eastern', score: 76.3, increase: 5.6 }
            ].map((store, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">{store.name}</span>
                  <span className="text-sm font-medium text-emerald-600 flex items-center">
                    <ArrowUp className="h-3 w-3 mr-1" />
                    {store.increase}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{store.region} Region</span>
                  <span>Current: {store.score}</span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-1.5 rounded-full bg-emerald-500" 
                    style={{ width: `${store.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview; 